import { ApiClient } from '@api/api-configuration';
import type { UserResponse } from '@api/READ_ONLY/users_api/Api';
import { FormWrapper } from '@app/features/form/Components/FormWrapper/FormWrapper';
import { getMyDoctorRecord } from '@app/features/doctorPortal/api';
import { getDoctorProfileFields, SELF_EDITABLE_FIELDS } from '@app/features/doctorPortal/doctorProfileFields';
import { useAuthContext } from '@context/auth/useAuthContext';
import useNotifications from '@context/notifications/useNotificationContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Modal, Skeleton, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The doctor's own account.
 *
 * The portal had no way to see it at all: a doctor could not read their own name,
 * email or specialisation anywhere, and the dashboard's one profile page is dead
 * code that never reached the router.
 *
 * ONLY THE TIME ZONE IS EDITABLE. `PUT /users/{id}` admits a non-admin on their
 * own record and then refuses, with a 403, every field outside
 * SELF_UPDATABLE_FIELDS — roles, enabled, email and the entity links all decide
 * what the account IS or what it can reach. The page narrows that further: name
 * is self-updatable server-side but the office keeps it, so it shows disabled
 * (see doctorProfileFields). A field that is not offered is rendered disabled
 * rather than left to fail on save.
 *
 * The doctor RECORD is read-only outright: `doctors_update_policy` is adm-only,
 * so specialisation, licence and commission are the office's to change and a
 * form offering them would be lying.
 *
 * This deliberately does NOT reuse the admin UserForm. That form's own reason to
 * exist is roles, the enabled switch and the caregiver/client/doctor pickers —
 * every one of which a doctor must not see, and two of which fetch admin-only
 * lists and would 403 on mount. It uses the same FormWrapper and the same field
 * shapes, so it is the same form machinery, minus the administration.
 */

const EMPTY = '—';

/** What the form holds. `email` is displayed and never sent — see doctorProfileFields. */
type ProfileFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  timezone: string;
};

const DoctorProfilePage = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotifications();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const [passwordPrompt, setPasswordPrompt] = useState(false);

  const account = useQuery({
    queryKey: ['doctor', 'my-account', user.userId],
    enabled: Boolean(user.userId),
    queryFn: async () => (await ApiClient.usersApi.users.getUserById(user.userId)).data,
  });

  // The `doctors` row behind the account — specialisation and the rest. Separate
  // from the user record because they are separate things: one is the login, the
  // other is the clinician the office keeps.
  const record = useQuery({
    queryKey: ['doctor', 'me'],
    queryFn: getMyDoctorRecord,
  });

  const fields = useMemo(() => getDoctorProfileFields(t), [t]);

  const defaultValues = useMemo(
    () => ({
      firstName: account.data?.firstName ?? '',
      lastName: account.data?.lastName ?? '',
      email: account.data?.email ?? '',
      timezone: account.data?.timezone ?? '',
    }),
    [account.data]
  );

  const save = useMutation({
    mutationFn: (values: ProfileFormValues) =>
      // ONLY the self-editable keys are sent. Not a convenience: the server
      // refuses the whole request if a single field outside its allow-list is
      // present, so posting the form wholesale would 403 on `email` — which this
      // page shows, and must show, read-only.
      ApiClient.usersApi.users.updateUser(
        user.userId,
        Object.fromEntries(SELF_EDITABLE_FIELDS.map((key) => [key, values[key]]))
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['doctor', 'my-account', user.userId] });
      openNotification('topRight', {
        title: t('doctor-profile-saved'),
        description: '',
        type: 'Success',
      });
    },
    onError: () =>
      openNotification('topRight', {
        title: t('doctor-profile-save-failed'),
        description: '',
        type: 'Danger',
      }),
  });

  const changePassword = useMutation({
    mutationFn: () => ApiClient.usersApi.users.updateUser(user.userId, { requiredActions: ['UPDATE_PASSWORD'] }),
    onSuccess: () => {
      setPasswordPrompt(false);
      void queryClient.invalidateQueries({ queryKey: ['doctor', 'my-account', user.userId] });
      openNotification('topRight', {
        title: t('doctor-profile-password-requested'),
        description: '',
        type: 'Success',
      });
    },
    onError: () =>
      openNotification('topRight', {
        title: t('doctor-profile-password-failed'),
        description: '',
        type: 'Danger',
      }),
  });

  if (account.isError) {
    return <Alert type="error" showIcon message={t('doctor-profile-load-failed')} />;
  }

  const pending = (account.data?.requiredActions ?? []) as UserResponse['requiredActions'];
  const passwordPending = pending?.includes('UPDATE_PASSWORD');

  return (
    // The layout's content area is `overflow-hidden` and every page owns its own
    // scrolling — the table pages do it by handing a GenericDataTable a
    // `flex-1 min-h-0` box. A page of cards has to say so itself, or anything past
    // the fold is simply clipped with no scrollbar.
    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4">
      <Card title={t('doctor-profile-account-title')}>
        {account.isLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <>
            <Typography.Text type="secondary" className="!text-[12px] block mb-3">
              {t('doctor-profile-editable-note')}
            </Typography.Text>
            <FormWrapper<ProfileFormValues>
              fields={fields}
              defaultValues={defaultValues}
              extraClasses=""
              // Explicit, because the default picks Save vs Create off an `:id`
              // in the route — and this route names no record, so it read
              // "Create" on a form that only ever updates.
              submitLabel={t('common-save')}
              onSubmit={(values: ProfileFormValues) => save.mutate(values)}
            />
          </>
        )}
      </Card>

      <Card title={t('doctor-profile-record-title')}>
        <Typography.Text type="secondary" className="!text-[12px] block mb-3">
          {t('doctor-profile-record-note')}
        </Typography.Text>
        {record.isLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : record.isError ? (
          <Alert type="warning" showIcon message={t('doctor-profile-record-load-failed')} />
        ) : (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t('doctor-profile-field-specialization')}>
              {record.data?.specialization || EMPTY}
            </Descriptions.Item>
            <Descriptions.Item label={t('doctor-profile-field-email')}>{record.data?.email || EMPTY}</Descriptions.Item>
            <Descriptions.Item label={t('doctor-profile-field-roles')}>
              {account.data?.roles?.length
                ? account.data.roles.map((role) => (
                    <Tag key={role.code} className="!m-0 !mr-1">
                      {role.name ?? role.code}
                    </Tag>
                  ))
                : EMPTY}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card title={t('doctor-profile-security-title')}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[13px] text-slate-700">{t('doctor-profile-password-label')}</div>
            <div className="text-[12px] text-slate-500">{t('doctor-profile-password-help')}</div>
          </div>
          {passwordPending ? (
            <Tag color="orange">{t('doctor-profile-password-pending')}</Tag>
          ) : (
            <Button onClick={() => setPasswordPrompt(true)}>{t('doctor-profile-password-action')}</Button>
          )}
        </div>
      </Card>

      <Modal
        open={passwordPrompt}
        onCancel={() => setPasswordPrompt(false)}
        onOk={() => changePassword.mutate()}
        okText={t('doctor-profile-password-confirm')}
        cancelText={t('common-cancel')}
        okButtonProps={{ loading: changePassword.isPending }}
        title={t('doctor-profile-password-action')}
        destroyOnHidden
      >
        {t('doctor-profile-password-confirm-body')}
      </Modal>
    </div>
  );
};

export default DoctorProfilePage;
